import React, { useEffect, useState, useRef } from "react";
import { api, formatApiError } from "@/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Copy, ExternalLink, CheckCircle2, AlertTriangle, XCircle, Play, Save, Eye, EyeOff, RefreshCw, Zap, ShieldCheck, RotateCw, FlaskConical, Lock } from "lucide-react";

export default function AdminPayPal() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ client_id: "", client_secret: "", mode: "live", webhook_id: "" });
  const [showSecret, setShowSecret] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState(null);
  const [diagnostics, setDiagnostics] = useState(null);
  const [logs, setLogs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  // v2.0.10 (Phase 31)
  const [autoSetupBusy, setAutoSetupBusy] = useState(false);
  const [simulateBusy, setSimulateBusy] = useState(false);
  const [syncJob, setSyncJob] = useState(null);      // { id, total, checked, activated, status }
  const [pendingSubs, setPendingSubs] = useState([]);
  const [pendingTotal, setPendingTotal] = useState(0);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [retryingSid, setRetryingSid] = useState(null);
  const [registered, setRegistered] = useState(null);
  const [prodUrl, setProdUrl] = useState("");
  const pollRef = useRef(null);

  const loadRegistered = async () => {
    try {
      const { data } = await api.get("/admin/paypal/webhook/registered");
      setRegistered(data);
    } catch (e) { /* silencieux */ }
  };

  const load = async () => {
    try {
      const [{ data: s }, { data: w }, { data: d }, { data: l }] = await Promise.all([
        api.get("/admin/paypal/settings"),
        api.get("/admin/paypal/webhook-url"),
        api.get("/admin/paypal/diagnostics"),
        api.get("/admin/paypal/webhook-logs?limit=30"),
      ]);
      setCfg(s);
      setForm({
        client_id: s.client_id || "",
        client_secret: "",  // vide, l'utilisateur peut ré-écrire s'il veut changer
        mode: s.mode || "live",
        webhook_id: s.webhook_id || "",
      });
      setWebhookUrl(w);
      setDiagnostics(d);
      setLogs(l);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); loadPending(); loadRegistered(); }, []);
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/paypal/settings", form);
      toast.success("Configuration PayPal sauvegardée (secret chiffré en BDD)");
      setForm({ ...form, client_secret: "" });
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const testConn = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/admin/paypal/test-connection");
      if (data.ok) toast.success(`✓ Connexion OK — token ${data.token_prefix}`);
      else toast.error(`✗ Échec — ${data.reason}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setTesting(false); }
  };

  const copyWebhookUrl = () => {
    if (!webhookUrl?.webhook_url) return;
    navigator.clipboard.writeText(webhookUrl.webhook_url);
    toast.success("URL webhook copiée dans le presse-papier");
  };

  // v2.0.10 — Auto-setup webhook via API PayPal
  const autoSetupWebhook = async (overrideUrl = null) => {
    if (!cfg?.enabled) { toast.error("Renseignez Client ID + Secret d'abord"); return; }
    setAutoSetupBusy(true);
    try {
      const { data } = await api.post("/admin/paypal/webhook/auto-setup",
        overrideUrl ? { url: overrideUrl } : {});
      if (data.ok === false) { toast.error(`Auto-Setup : ${data.reason || "erreur inconnue"}`); }
      else if (data.action === "unchanged") { toast.info(`Webhook déjà à jour · ID ${data.webhook_id?.slice(0, 12)}… · aucun changement`); }
      else { toast.success(`✓ Webhook ${data.action === "created" ? "CRÉÉ" : "MIS À JOUR"} · ID ${data.webhook_id?.slice(0, 12)}… · ${data.events_count} événements`); }
      await load();
      await loadRegistered();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setAutoSetupBusy(false); }
  };

  // v2.0.10 — Simule un webhook (test bout-en-bout)
  const simulateWebhook = async (event_type, subscription_id = null) => {
    setSimulateBusy(true);
    try {
      const { data } = await api.post("/admin/paypal/webhook/simulate", { event_type, subscription_id });
      toast.success(`✓ Simulé ${event_type} · ${data.handler_result || "traité"}`);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSimulateBusy(false); }
  };

  // v2.0.10 — Liste abonnements en attente
  const loadPending = async () => {
    setPendingLoading(true);
    try {
      const [{ data }, { data: c }] = await Promise.all([
        api.get("/admin/paypal/subscriptions/pending?limit=50"),
        api.get("/admin/paypal/subscriptions/pending-count"),
      ]);
      setPendingSubs(data);
      setPendingTotal(c?.total ?? data.length);
    } catch (e) { /* silent */ }
    finally { setPendingLoading(false); }
  };

  // v2.0.10 — Retry individuel
  const retryOne = async (sid) => {
    setRetryingSid(sid);
    try {
      const { data } = await api.post(`/admin/paypal/subscriptions/${sid}/retry`);
      if (data.ok === false) { toast.error(`${sid} · ${data.reason || "erreur"}`); }
      else if (data.activated) { toast.success(`✓ ${sid} activé !`); }
      else { toast.info(`${sid} · statut PayPal: ${data.paypal_status}`); }
      await loadPending();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setRetryingSid(null); }
  };

  // v2.0.10 — Batch sync (job asynchrone + polling)
  const startBatchSync = async () => {
    if (!window.confirm("Lancer la synchronisation batch de tous les abonnements en attente ? Cette opération peut prendre 1-2 minutes.")) return;
    try {
      const { data } = await api.post("/admin/paypal/subscriptions/sync-all?limit=300");
      setSyncJob({ id: data.job_id, total: data.total, checked: 0, activated: 0, status: "running" });
      toast.info(`Sync démarrée — ${data.total} abonnements à vérifier`);
      // Poll toutes les 3s
      pollRef.current = setInterval(async () => {
        try {
          const { data: st } = await api.get(`/admin/paypal/subscriptions/sync-status/${data.job_id}`);
          setSyncJob(st);
          if (st.status !== "running") {
            clearInterval(pollRef.current);
            pollRef.current = null;
            if (st.status === "done") {
              toast.success(`✓ Sync terminée · ${st.activated} activés / ${st.checked} vérifiés · ${(st.errors || []).length} erreurs`);
              await loadPending();
              await load();
            } else {
              toast.error(`✗ Sync interrompue: ${st.error || "erreur"}`);
            }
          }
        } catch { /* keep polling */ }
      }, 3000);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="min-h-full">
      <PageHeader eyebrow="Paiement" title="Configuration PayPal" sub="Renseignez ici votre Client ID, Secret Key et Webhook ID. La modification est appliquée immédiatement — pas besoin de redémarrer le backend." />
      <div className="p-6 lg:p-10 space-y-6">

        {/* Status header */}
        <div className={`p-5 border-2 ${cfg?.enabled ? "border-[#059669] bg-emerald-50" : "border-[#E60000] bg-red-50"}`} data-testid="pp-status">
          <div className="flex items-center gap-4">
            {cfg?.enabled ? <CheckCircle2 className="h-10 w-10 text-[#059669]" /> : <XCircle className="h-10 w-10 text-[#E60000]" />}
            <div className="flex-1">
              <div className={`font-display font-black text-xl tracking-tighter ${cfg?.enabled ? "text-[#065F46]" : "text-[#7F1D1D]"}`}>
                {cfg?.enabled ? "PayPal opérationnel" : "PayPal non configuré"}
              </div>
              <div className="text-sm text-[#495057]">
                Mode : <b className="uppercase">{cfg?.mode || "—"}</b> ·
                Base API : <span className="font-mono text-xs">{cfg?.base_url || "—"}</span> ·
                Webhook ID : {cfg?.has_webhook_id ? <b className="text-[#059669]">✓ configuré</b> : <b className="text-[#E60000]">✗ manquant</b>}
              </div>
              {cfg?.source_env_only && (
                <div className="mt-1 text-[10px] text-[#0EA5E9]">ℹ️ Config actuelle chargée depuis <code>.env</code> — sauvegardez pour la persister en BDD.</div>
              )}
            </div>
            <Button onClick={testConn} disabled={testing || !cfg?.enabled} data-testid="pp-test-conn" className="bg-[#0070BA] text-white">
              <Play className="h-4 w-4 mr-2" />{testing ? "Test…" : "Tester la connexion"}
            </Button>
          </div>
        </div>

        {/* Instructions Webhook */}
        <div className="p-5 bg-[#FFF9DB] border-2 border-[#FFD700]" data-testid="pp-webhook-instructions">
          <div className="font-display font-bold text-lg mb-3">📮 URL webhook à configurer côté PayPal</div>
          <div className="text-sm text-[#495057] mb-3">
            Dans <b>PayPal Developer</b> → Apps & Credentials → cliquez sur votre application → onglet <b>Webhooks</b> →
            <b> Add Webhook</b>. Collez l'URL ci-dessous, puis cochez les événements recommandés.
          </div>
          <div className="flex items-center gap-2 mb-3">
            <code className="flex-1 p-3 bg-white border border-[#0A0A0A] font-mono text-xs break-all" data-testid="pp-webhook-url">
              {webhookUrl?.webhook_url || "—"}
            </code>
            <Button variant="outline" onClick={copyWebhookUrl} data-testid="pp-copy-webhook" className="h-11"><Copy className="h-4 w-4 mr-1" />Copier</Button>
            <Button onClick={autoSetupWebhook} disabled={autoSetupBusy || !cfg?.enabled} data-testid="pp-auto-setup-webhook" className="h-11 bg-[#0070BA] text-white">
              <Zap className="h-4 w-4 mr-1" />{autoSetupBusy ? "Config…" : "Auto-Setup"}
            </Button>
            <a href="https://developer.paypal.com/dashboard/applications/live" target="_blank" rel="noreferrer" className="h-11 inline-flex items-center px-3 border border-[#0A0A0A] text-xs font-semibold hover:bg-[#0A0A0A] hover:text-white"><ExternalLink className="h-4 w-4 mr-1" />PayPal Dev</a>
          </div>
          {webhookUrl?.warning && (
            <div className="mb-3 p-2 bg-white border-l-4 border-[#E60000] text-xs text-[#7F1D1D]" data-testid="pp-webhook-warning">
              {webhookUrl.warning}
            </div>
          )}
          <div className="text-[10px] uppercase tracking-wider font-bold text-[#495057] mb-1">Événements recommandés à cocher :</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-1 text-[10px] font-mono">
            {(webhookUrl?.events_recommended || []).map((e) => (
              <div key={e} className="p-1.5 bg-white border border-[#DEE2E6]">{e}</div>
            ))}
          </div>
          <div className="mt-3 text-[10px] text-[#495057]">
            Une fois la webhook créée dans PayPal, copiez son <b>Webhook ID</b> (format <code>0X...</code>) et collez-le dans le formulaire ci-dessous.
            {webhookUrl?.detected_from === "origin" && <span className="ml-2 text-[#059669]">✓ URL détectée depuis votre navigateur actuel.</span>}
          </div>

          {/* v2.0.20 — Cibler explicitement l'URL de production */}
          <div className="mt-4 pt-3 border-t border-[#FFD700]">
            <div className="text-[10px] uppercase tracking-wider font-bold text-[#495057] mb-1">
              Enregistrer une autre URL (domaine de production)
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Input value={prodUrl} onChange={(e) => setProdUrl(e.target.value)}
                     placeholder="https://parcavenue.avenuerh.pro" data-testid="pp-prod-url"
                     className="flex-1 min-w-[240px] h-11 bg-white font-mono text-xs" />
              <Button onClick={() => autoSetupWebhook(prodUrl)} disabled={autoSetupBusy || !cfg?.enabled || !prodUrl}
                      data-testid="pp-auto-setup-prod" className="h-11 bg-[#0A0A0A] text-white">
                <Zap className="h-4 w-4 mr-1" />Enregistrer cette URL
              </Button>
            </div>
            <div className="text-[10px] text-[#495057] mt-1">
              Le chemin <code>/api/public/paypal/webhook</code> est ajouté automatiquement. Le Webhook ID est
              enregistré en base, la vérification de signature devient active immédiatement.
            </div>
          </div>

          {/* Webhooks réellement enregistrés côté PayPal */}
          <div className="mt-4 pt-3 border-t border-[#FFD700]">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-wider font-bold text-[#495057]">
                Webhooks enregistrés sur votre compte PayPal
              </div>
              <Button size="sm" variant="outline" onClick={loadRegistered} data-testid="pp-load-registered" className="text-[10px] h-7">
                <RefreshCw className="h-3 w-3 mr-1" />Rafraîchir
              </Button>
            </div>
            {registered?.ok === false && (
              <div className="text-[11px] text-[#7F1D1D]" data-testid="pp-registered-error">{registered.reason}</div>
            )}
            <div className="space-y-1">
              {(registered?.webhooks || []).map((w) => (
                <div key={w.id} className={`p-2 border text-[11px] flex flex-wrap items-center gap-2 ${
                  w.is_current ? "bg-white border-[#059669]" : "bg-white border-[#DEE2E6]"
                }`} data-testid={`pp-registered-${w.id}`}>
                  <code className="font-mono break-all flex-1 min-w-[200px]">{w.url}</code>
                  <span className="font-mono text-[10px] text-[#868E96]">{w.id}</span>
                  <span className="text-[10px] text-[#868E96]">{w.events.length} évts</span>
                  {w.is_current
                    ? <span className="text-[9px] uppercase font-bold px-2 py-0.5 bg-[#059669] text-white">actif</span>
                    : <Button size="sm" variant="outline" className="text-[9px] h-6"
                              data-testid={`pp-use-${w.id}`}
                              onClick={() => autoSetupWebhook(w.url)}>Utiliser</Button>}
                </div>
              ))}
              {registered?.ok && (registered.webhooks || []).length === 0 && (
                <div className="text-[11px] text-[#868E96]" data-testid="pp-registered-empty">Aucun webhook enregistré.</div>
              )}
            </div>
          </div>
        </div>

        {/* Diagnostics */}
        {diagnostics && (
          <div className="p-5 bg-white border border-[#DEE2E6]" data-testid="pp-diagnostics">
            <div className="flex items-center justify-between mb-3">
              <div className="font-display font-bold text-lg">🩺 Diagnostic</div>
              <Button size="sm" variant="outline" onClick={load} data-testid="pp-refresh"><RefreshCw className="h-3 w-3 mr-1" />Rafraîchir</Button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
              <div className="p-3 border border-[#DEE2E6]">
                <div className="text-[10px] uppercase tracking-wider font-bold text-[#868E96]">Subscriptions</div>
                <div className="font-display font-black text-2xl mt-1">{diagnostics.subscriptions.total}</div>
                <div className="text-[10px] text-[#059669]">{diagnostics.subscriptions.active} actives</div>
              </div>
              <div className="p-3 border border-[#DEE2E6]">
                <div className="text-[10px] uppercase tracking-wider font-bold text-[#868E96]">Webhooks 24h</div>
                <div className="font-display font-black text-2xl mt-1">{diagnostics.webhooks.last_24h}</div>
                <div className="text-[10px] text-[#495057]">{diagnostics.webhooks.last_7d} sur 7j</div>
              </div>
              <div className="p-3 border border-[#DEE2E6]">
                <div className="text-[10px] uppercase tracking-wider font-bold text-[#868E96]">Signature OK</div>
                <div className="font-display font-black text-2xl mt-1 text-[#059669]">{diagnostics.webhooks.verified_ok}</div>
                <div className="text-[10px] text-[#E60000]">{diagnostics.webhooks.verified_ko} rejetés</div>
              </div>
              <div className="p-3 border border-[#DEE2E6]">
                <div className="text-[10px] uppercase tracking-wider font-bold text-[#868E96]">Dernière activation</div>
                <div className="font-mono text-[10px] mt-1 truncate">{diagnostics.last_activation?.subscription_id || "—"}</div>
                <div className="text-[10px] text-[#868E96] mt-0.5">{diagnostics.last_activation?.activated_at?.slice(0, 16).replace("T", " ") || "—"}</div>
              </div>
            </div>
            {diagnostics.hints?.length > 0 && (
              <div className="space-y-1" data-testid="pp-hints">
                {diagnostics.hints.map((h, i) => (
                  <div key={i} className="text-xs p-2 bg-[#F1F3F5] border-l-2 border-[#0EA5E9]">{h}</div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Formulaire config */}
        <div className="p-5 bg-white border border-[#DEE2E6]" data-testid="pp-form">
          <div className="font-display font-bold text-lg mb-4">⚙️ Configuration PayPal</div>
          <div className="grid gap-4">
            <div>
              <Label className="text-[10px] uppercase tracking-wider">Client ID *</Label>
              <div className="text-[10px] text-[#868E96] mb-1">Visible dans PayPal Developer → Apps & Credentials → votre app.</div>
              <Input value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} placeholder="AXxxxx..." data-testid="pp-client-id" className="font-mono text-xs" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Label className="text-[10px] uppercase tracking-wider">Secret Key *</Label>
                <span data-testid="pp-secret-encrypted" className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider bg-[#059669] text-white px-1.5 py-0.5">
                  <Lock className="h-2.5 w-2.5" /> Chiffré Fernet
                </span>
              </div>
              <div className="text-[10px] text-[#868E96] mb-1">
                {cfg?.has_client_secret ? <>Actuel : <code className="text-[#0A0A0A]">{cfg.client_secret_masked}</code>. Laissez vide pour conserver.</> : "Aucun secret configuré."}
              </div>
              <div className="flex gap-1">
                <Input
                  type={showSecret ? "text" : "password"}
                  value={form.client_secret}
                  onChange={(e) => setForm({ ...form, client_secret: e.target.value })}
                  placeholder={cfg?.has_client_secret ? "•••••••••••••••••••••••" : "EFxxxxx..."}
                  data-testid="pp-client-secret"
                  className="font-mono text-xs"
                />
                <Button type="button" variant="outline" onClick={() => setShowSecret(!showSecret)} data-testid="pp-toggle-secret" className="h-10 px-3">
                  {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-wider">Mode *</Label>
                <Select value={form.mode} onValueChange={(v) => setForm({ ...form, mode: v })}>
                  <SelectTrigger data-testid="pp-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="live">🟢 LIVE (production, vrais paiements)</SelectItem>
                    <SelectItem value="sandbox">🧪 SANDBOX (test, faux paiements)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider">Webhook ID</Label>
                <div className="text-[10px] text-[#868E96] mb-1">Après avoir créé le webhook, PayPal fournit un ID (ex: <code>0XX...</code>).</div>
                <Input value={form.webhook_id} onChange={(e) => setForm({ ...form, webhook_id: e.target.value })} placeholder="0XX..." data-testid="pp-webhook-id" className="font-mono text-xs" />
              </div>
            </div>
          </div>
          <div className="mt-6 flex gap-2 justify-end">
            <Button onClick={save} disabled={saving} data-testid="pp-save" className="bg-[#059669] text-white">
              <Save className="h-4 w-4 mr-2" />{saving ? "Sauvegarde…" : "Enregistrer & Appliquer"}
            </Button>
          </div>
        </div>

        {/* v2.0.10 — Tester Webhook Live (simulation) */}
        <div className="p-5 bg-white border border-[#DEE2E6]" data-testid="pp-simulate">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="font-display font-bold text-lg flex items-center gap-2">
                <FlaskConical className="h-5 w-5 text-[#7C3AED]" />
                Tester la chaîne webhook (simulation live)
              </div>
              <div className="text-xs text-[#495057]">Envoie un événement PayPal factice au handler interne pour valider bout-en-bout sans attendre de vrai paiement.</div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => simulateWebhook("BILLING.SUBSCRIPTION.ACTIVATED")} disabled={simulateBusy} data-testid="pp-sim-activated" className="bg-[#059669] text-white">
              <Play className="h-4 w-4 mr-1" />ACTIVATED (sans sid)
            </Button>
            <Button onClick={() => simulateWebhook("BILLING.SUBSCRIPTION.CANCELLED")} disabled={simulateBusy} data-testid="pp-sim-cancelled" variant="outline">
              CANCELLED
            </Button>
            <Button onClick={() => simulateWebhook("BILLING.SUBSCRIPTION.PAYMENT.FAILED")} disabled={simulateBusy} data-testid="pp-sim-failed" variant="outline">
              PAYMENT.FAILED
            </Button>
          </div>
          <div className="mt-2 text-[10px] text-[#868E96]">💡 Les simulations sont marquées <code>simulated=true</code> dans les webhook logs pour ne pas polluer les stats.</div>
        </div>

        {/* v2.0.10 — Batch Sync + liste abonnements en attente */}
        <div className="p-5 bg-white border border-[#DEE2E6]" data-testid="pp-batch-sync">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="font-display font-bold text-lg flex items-center gap-2">
                <RotateCw className="h-5 w-5 text-[#F59E0B]" />
                Abonnements en attente ({pendingTotal})
              </div>
              <div className="text-xs text-[#495057]">Rattrape les paiements dont le webhook a été manqué (URL périmée, etc.). Interroge PayPal et active si status ACTIVE. Les 50 plus récents affichés ci-dessous · <b>Sync tous</b> traite le reste.</div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={loadPending} disabled={pendingLoading} data-testid="pp-pending-refresh">
                <RefreshCw className="h-3 w-3 mr-1" />Rafraîchir
              </Button>
              <Button onClick={startBatchSync} disabled={!!syncJob?.status && syncJob.status === "running"} data-testid="pp-sync-all" className="bg-[#E60000] text-white">
                <ShieldCheck className="h-4 w-4 mr-1" />Sync tous
              </Button>
            </div>
          </div>

          {/* Barre de progression du job */}
          {syncJob && (
            <div className="mb-3 p-3 bg-[#F1F3F5] border-l-4 border-[#0EA5E9]" data-testid="pp-sync-progress">
              <div className="flex items-center justify-between text-xs mb-1">
                <div>Job {syncJob.id?.slice(0, 8)}… · <b>{syncJob.status}</b></div>
                <div className="font-mono">{syncJob.checked}/{syncJob.total} · {syncJob.activated} activés · {(syncJob.errors || []).length} erreurs</div>
              </div>
              <div className="h-2 bg-white border border-[#DEE2E6]">
                <div className="h-full bg-[#059669] transition-all" style={{ width: `${syncJob.total ? Math.round(100 * syncJob.checked / syncJob.total) : 0}%` }} />
              </div>
            </div>
          )}

          {pendingSubs.length === 0 ? (
            <div className="p-4 text-center text-sm text-[#495057] bg-emerald-50 border border-emerald-200">
              <CheckCircle2 className="h-6 w-6 mx-auto mb-1 text-[#059669]" />
              Aucun abonnement en attente ✓
            </div>
          ) : (
            <div className="divide-y divide-[#DEE2E6] max-h-96 overflow-y-auto border border-[#DEE2E6]">
              {pendingSubs.slice(0, 30).map((s) => (
                <div key={s.subscription_id} className="p-2 grid grid-cols-1 md:grid-cols-5 gap-2 text-xs items-center hover:bg-[#F8F9FA]">
                  <div className="font-mono text-[10px] truncate md:col-span-2">{s.subscription_id}</div>
                  <div className="text-[10px] uppercase tracking-wider font-bold bg-[#F59E0B] text-white px-1.5 py-0.5 inline-block">{s.status}</div>
                  <div className="text-[10px] text-[#868E96] font-mono">{s.created_at?.slice(0, 16).replace("T", " ")}</div>
                  <div className="text-right">
                    <Button size="sm" variant="outline" onClick={() => retryOne(s.subscription_id)} disabled={retryingSid === s.subscription_id} data-testid={`pp-retry-${s.subscription_id}`}>
                      {retryingSid === s.subscription_id ? "…" : "Retry"}
                    </Button>
                  </div>
                </div>
              ))}
              {pendingSubs.length > 30 && <div className="p-2 text-center text-[10px] text-[#868E96]">+ {pendingSubs.length - 30} autres — utilisez "Sync tous" pour tout traiter</div>}
            </div>
          )}
        </div>

        {/* Logs webhooks reçus */}
        <div className="bg-white border border-[#DEE2E6]" data-testid="pp-logs">
          <div className="p-4 border-b border-[#DEE2E6] flex items-center justify-between">
            <div className="font-display font-bold">📬 Derniers webhooks PayPal reçus ({logs.length})</div>
            <div className="text-[10px] text-[#868E96]">Utile pour vérifier que PayPal atteint bien votre serveur.</div>
          </div>
          {logs.length === 0 ? (
            <div className="p-6 text-center text-sm text-[#495057]">
              <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-[#F59E0B]" />
              Aucun webhook reçu à ce jour. Assurez-vous que l'URL webhook (ci-dessus) est bien configurée côté PayPal Dashboard.
            </div>
          ) : (
            <div className="divide-y divide-[#DEE2E6]">
              {logs.map((w) => (
                <div key={w.id} className="p-3 grid grid-cols-1 md:grid-cols-4 gap-2 text-xs">
                  <div className="font-mono text-[10px]">{w.received_at?.slice(0, 19).replace("T", " ")}</div>
                  <div className="font-semibold">{w.event_type}</div>
                  <div className="font-mono text-[10px] truncate">{w.subscription_id || "—"}</div>
                  <div>
                    {w.verified === true && <span className="text-[10px] uppercase tracking-wider font-bold bg-[#059669] text-white px-1.5 py-0.5">✓ Signature OK</span>}
                    {w.verified === false && <span className="text-[10px] uppercase tracking-wider font-bold bg-[#E60000] text-white px-1.5 py-0.5">✗ Signature KO</span>}
                    {w.verified === null && <span className="text-[10px] uppercase tracking-wider font-bold bg-[#868E96] text-white px-1.5 py-0.5">Non vérifiée</span>}
                    {w.resource_status && <span className="ml-1 text-[10px] text-[#495057]">· {w.resource_status}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
